// The counts behind "Needs attention", and the number on the Appeals card, asked
// so that a refusal can never end the person's session.
//
// The home screen is where everyone lands, and it asks these questions on the person's
// behalf: nobody chose to open the appeals lists or the moderation queue. The web
// client treats any 401 as "signed out" (services/api's response interceptor), and
// these routes can answer 401 to someone who is signed in: the moderation queue does
// whenever it cannot work out who is calling, which a slow directory lookup is enough
// to cause, and the appeals lists do for a caller they cannot name. Asked plainly from
// the landing page, that would sign people out of the whole app for opening it.
//
// `skipSessionExpiry` is the option the Stores page's two probes carry for the same
// reason (storeRequestsAPI.summary, managerStoreAccessAPI.access). Every non-2xx here
// means one thing: no number for that row.
//
// Each call below is the SAME request as its twin in services, apart from that option;
// quietReads.test.ts holds the two together. The store-requests count needs nothing
// here: storeRequestsAPI.summary already carries the option. So does every call in
// homeCountsAPI, the counts the server works out for this screen alone (exit reviews,
// improvement plans, probation, joiners with no buddy, training not marked, stores to
// fix) and the three it keeps in the background (pending requests, people absent today,
// stores short of people).
import api from '../../services/api';

type QuietConfig = NonNullable<Parameters<typeof api.get>[1]> & { skipSessionExpiry: true };

const quiet = (params: Record<string, unknown>): QuietConfig => ({ params, skipSessionExpiry: true });

/** An appeal nobody has decided yet is in one of these two states; the lists take one status per request. */
export const UNDECIDED = ['pending', 'under_review'] as const;
type Undecided = (typeof UNDECIDED)[number];

/** Twin of managerFocusAPI.getAppealWorkflowReport: the Red Flags Appeals list's own call, for one status. */
export const redAppealsWaiting = (params: { projectId: number; status: Undecided }) =>
  api.get('/manager-focus/appeals/report', quiet(params));

/** Twin of yellowFlagAppealsAPI.adminListAppeals: the Yellow Flag Appeals list's own call, for one status. */
export const yellowAppealsWaiting = (params: { projectId: number; status: Undecided }) =>
  api.get('/yellow-flags/admin/appeals', quiet(params));

/** Twin of socialModerationAPI.getQueue. */
export const reportedPostsQueue = (params: { projectId: number; status: string; from: string; to: string }) =>
  api.get('/social-moderation/queue', quiet(params));

/** Twin of connectPolicyAPI.listAiReviews, on its smallest page: only the counts are read. */
export const aiReviewWorklist = (params: { projectId: number; filter: 'needs_review'; limit: number }) =>
  api.get('/social-moderation/ai-reviews', quiet(params));

/**
 * Twin of appealsDashboardAPI.getDashboard: the Appeals dashboard's one call, with the
 * month and Role filter it opens on. Like the appeals lists above, the route answers 401
 * to a caller it cannot name.
 */
export const appealsDashboard = (params: { month: string; projectId: number; roleId?: string }) =>
  api.get('/manager-focus/dashboard/appeals', quiet(params));
